import { useEffect, useState } from "preact/hooks";
import type { Group } from "../cloud/api";
import type { GroupSummary, InstallRunner } from "../flow/runner";
import { MAX_MEMBERS } from "../groups/forwarding";
import { t, locale } from "../i18n";
import { EsimNote } from "./SimChecklist";

/**
 * Step 1 (v3): pick an existing line group or create one. A new group makes this phone
 * member 1 with the main number; an existing group appends it as the new last member.
 * Also the entry points for Replace (install path), Recovery and Remove (no phone needed).
 */
export function GroupStep({ runner }: { runner: InstallRunner }) {
  const [groups, setGroups] = useState<GroupSummary[] | undefined>();
  const [simText, setSimText] = useState<string | undefined>();
  const [selected, setSelected] = useState<GroupSummary | undefined>();
  const [detail, setDetail] = useState<Group | undefined>();
  const [name, setName] = useState("");
  const [mainNumber, setMainNumber] = useState("");
  const [position, setPosition] = useState<number | undefined>();
  const [leaveUnlocked, setLeaveUnlocked] = useState(false);
  const [force, setForce] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void runner.listGroups().then((r) => {
      setGroups(r?.groups ?? []);
      const txt = r?.sim_requirements;
      if (txt) setSimText(locale() === "ar" ? txt.ar : txt.en);
    });
  }, [runner]);
  useEffect(() => {
    setDetail(undefined);
    setPosition(undefined);
    if (!selected) return;
    void runner.inspectGroup(selected.id).then(setDetail);
  }, [runner, selected]);

  const create = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    const g = await runner.createGroup(name.trim(), mainNumber);
    setBusy(false);
    if (g) {
      const summary: GroupSummary = { id: g.id, name: g.name, main_number: g.main_number, member_count: 0, status: g.status };
      setGroups((gs) => [...(gs ?? []), summary]);
      setName("");
      setMainNumber("");
      setSelected(summary);
    }
  };
  const count = detail?.members.length ?? selected?.member_count ?? 0;
  const full = count >= MAX_MEMBERS;
  const go = async (intent: "add" | "replace") => {
    if (!selected) return;
    setBusy(true);
    await runner.selectGroup(selected, count === 0 ? "create" : intent, position);
    setBusy(false);
  };
  const recover = async (mode: "reflash" | "stock") => {
    if (!selected) return;
    setBusy(true);
    await runner.startRecovery(selected, mode, leaveUnlocked, position);
    setBusy(false);
  };
  const remove = async () => {
    if (!selected || position === undefined) return;
    setBusy(true);
    await runner.startRemoval(selected, position, force);
    setBusy(false);
  };
  const positionPicker = (id: string, label: string) => (
    <div>
      <label for={id}>{label}</label>
      <select id={id} value={position ?? ""} onChange={(e) => setPosition(Number((e.target as HTMLSelectElement).value) || undefined)} disabled={!detail}>
        <option value="">{t("group.position_pick")}</option>
        {(detail?.members ?? []).map((m) => (
          <option key={m.position} value={m.position}>{t("group.position_option", { position: m.position, number: m.number, role: t(`fwd.role.${m.role}`) })}</option>
        ))}
      </select>
    </div>
  );

  return (
    <div class="panel">
      <h2 tabIndex={-1}>{t("group.heading")}</h2>
      <p>{t("group.intro")}</p>
      {groups === undefined ? (
        <p role="status">{t("common.loading")}</p>
      ) : groups.length === 0 ? (
        <p>{t("group.none")}</p>
      ) : (
        <fieldset class="stack" style="border:0;padding:0;margin:0">
          <legend class="sr-only">{t("group.heading")}</legend>
          {groups.map((g) => (
            <label key={g.id} style="font-weight:400">
              <input type="radio" name="group" value={g.id} checked={selected?.id === g.id} onChange={() => setSelected(g)} /> <strong>{g.name}</strong> · <code class="ltr">{g.main_number}</code>{" "}
              <span class="muted">— {t("group.members_count", { n: g.member_count, max: MAX_MEMBERS })} · {t(`group.status.${g.status}`)}</span>
            </label>
          ))}
        </fieldset>
      )}
      <form onSubmit={(e) => void create(e)} class="row" style="margin-top:1rem;align-items:flex-end">
        <div style="flex:1">
          <label for="new-group">{t("group.create_name")}</label>
          <input id="new-group" type="text" value={name} onInput={(e) => setName((e.target as HTMLInputElement).value)} />
        </div>
        <div style="flex:1">
          <label for="new-group-number">{t("group.create_number")}</label>
          <input id="new-group-number" class="ltr" type="tel" placeholder="+9715…" value={mainNumber} onInput={(e) => setMainNumber((e.target as HTMLInputElement).value)} aria-describedby="new-group-hint" />
        </div>
        <button type="submit" disabled={busy || !name.trim() || !mainNumber.trim()}>{t("group.create_button")}</button>
      </form>
      <p id="new-group-hint" class="hint">{t("group.create_hint")}</p>

      <section class="notice" aria-labelledby="sim-req-h">
        <h3 id="sim-req-h">{t("group.sim_req.heading")}</h3>
        <ul>
          <li>{t("group.sim_req.licence")}</li>
          <li>{t("group.sim_req.forwarding")}</li>
          <li>{t("group.sim_req.billing")}</li>
          <li>{t("group.sim_req.visible")}</li>
        </ul>
        {simText && <p class="hint">{simText}</p>}
      </section>

      {selected && (
        <section class="notice" aria-live="polite">
          <p>
            {count === 0
              ? t("group.plan_create", { group: selected.name, number: selected.main_number })
              : full
                ? t("group.plan_full", { group: selected.name, max: MAX_MEMBERS })
                : t("group.plan_add", { group: selected.name, position: count + 1, previous: count })}
          </p>
          <div class="row">
            <button type="button" class="primary" disabled={busy || full} onClick={() => void go("add")}>{count === 0 ? t("group.use_create") : t("group.use_add")}</button>
          </div>
          {count > 0 && (
            <details style="margin-top:0.75rem">
              <summary>{t("group.replace_heading")}</summary>
              <p class="hint">{t("group.replace_hint")}</p>
              {positionPicker("replace-pos", t("group.replace_position"))}
              <button type="button" disabled={busy || position === undefined} onClick={() => void go("replace")}>{t("group.replace_button")}</button>
            </details>
          )}
        </section>
      )}

      <details style="margin-top:1rem">
        <summary>{t("group.recovery_heading")}</summary>
        <p class="hint">{t("group.recovery_hint")}</p>
        <EsimNote where="recovery" />
        {positionPicker("recovery-pos", t("group.recovery_position"))}
        <label style="font-weight:400">
          <input type="checkbox" checked={leaveUnlocked} onChange={(e) => setLeaveUnlocked((e.target as HTMLInputElement).checked)} /> {t("group.recovery_leave_unlocked")}
        </label>
        <div class="row" style="margin-top:0.5rem">
          <button type="button" disabled={busy || !selected || position === undefined} onClick={() => void recover("reflash")}>{t("group.recovery_reflash")}</button>
          <button type="button" disabled={busy || !selected} onClick={() => void recover("stock")}>{t("group.recovery_stock")}</button>
        </div>
        <p class="hint">{t("group.recovery_remove_first")}</p>
      </details>

      <details style="margin-top:1rem">
        <summary>{t("group.remove_heading")}</summary>
        <p class="hint">{t("group.remove_hint")}</p>
        {positionPicker("remove-pos", t("group.remove_position"))}
        <label style="font-weight:400">
          <input type="checkbox" checked={force} onChange={(e) => setForce((e.target as HTMLInputElement).checked)} /> {t("group.remove_force")}
        </label>
        <div class="row" style="margin-top:0.5rem">
          <button type="button" class="danger" disabled={busy || !selected || position === undefined} onClick={() => void remove()}>{t("group.remove_button")}</button>
        </div>
      </details>
    </div>
  );
}
